import "./CreateCategory.css";
import React, { useState, useEffect } from "react";
import { Formik, useFormikContext } from "formik";
import * as Yup from "yup";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  FormControlLabel,
  Switch,
} from "@mui/material";
import TextFieldInput from "../../../../Components/Atoms/Inputs/TextField/TextFieldInput";
import PrimaryButton from "../../../../Components/Atoms/Buttons/Primary/PrimaryButton";
import useSave from "../../../../Hooks/useSave";
import useUpdate from "../../../../Hooks/useUpdate";
import { BarLoader } from "react-spinners";

// Helper component to auto-generate membersGroupPrices based on min/max
const AutoGenerateMemberGroups = ({ initializePrices }) => {
  const { values, setFieldValue } = useFormikContext();

  useEffect(() => {
    if (values.paymentType === "membersGroup") {
      const min = parseInt(values.minMemberCount);
      const max = parseInt(values.maxMemberCount);

      if (!isNaN(min) && !isNaN(max) && min > 0 && max >= min) {
        // Preserve existing prices if available
        const existingPricesMap = {};
        if (
          values.membersGroupPrices &&
          Array.isArray(values.membersGroupPrices)
        ) {
          values.membersGroupPrices.forEach((item) => {
            if (item.count) {
              existingPricesMap[item.count] = item.prices;
            }
          });
        }

        // Calculate expected counts
        const expectedCounts = [];
        for (let count = min; count <= max; count++) {
          expectedCounts.push(count.toString());
        }
        expectedCounts.sort();

        // Check current counts
        const currentCounts =
          values.membersGroupPrices && Array.isArray(values.membersGroupPrices)
            ? values.membersGroupPrices
                .map((item) => item.count)
                .filter(Boolean)
                .sort()
            : [];

        // Only update if counts don't match
        const countsMatch =
          expectedCounts.length === currentCounts.length &&
          expectedCounts.every(
            (count, index) => count === currentCounts[index]
          );

        if (!countsMatch) {
          const newPrices = expectedCounts.map((count) => ({
            count: count,
            prices: existingPricesMap[count] || initializePrices(),
          }));
          setFieldValue("membersGroupPrices", newPrices);
        }
      } else if (
        values.membersGroupPrices &&
        values.membersGroupPrices.length > 0 &&
        (isNaN(min) || isNaN(max) || min <= 0 || max < min)
      ) {
        // Clear prices if min/max are invalid
        setFieldValue("membersGroupPrices", []);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    values.minMemberCount,
    values.maxMemberCount,
    values.paymentType,
    setFieldValue,
  ]);

  return null;
};

// Base validation schema - will be enhanced with currency validation
const baseCreateCategorySchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Category name must be at least 2 characters")
    .required("Category name is required"),
  paymentType: Yup.string()
    .oneOf(["team", "member", "membersGroup"], "Invalid payment type")
    .required("Payment type is required"),
  minMemberCount: Yup.number().when("paymentType", {
    is: "membersGroup",
    then: (schema) =>
      schema
        .required("Min member count is required")
        .min(1, "Min member count must be at least 1")
        .integer("Min member count must be an integer"),
    otherwise: (schema) => schema,
  }),
  maxMemberCount: Yup.number()
    .when("paymentType", {
      is: "membersGroup",
      then: (schema) =>
        schema
          .required("Max member count is required")
          .min(1, "Max member count must be at least 1")
          .integer("Max member count must be an integer"),
      otherwise: (schema) => schema,
    })
    .when("minMemberCount", (minMemberCount, schema) => {
      if (minMemberCount && typeof minMemberCount === "number") {
        return schema.min(
          minMemberCount,
          "Max member count must be greater than or equal to min member count"
        );
      }
      return schema;
    }),
});

function CreateCategory({
  navigate,
  categoriesURL,
  categoryId,
  category,
  isEdit = false,
  loading: categoryLoading = false,
  currencies = [],
  currenciesLoading = false,
}) {
  const [goSave, setGoSave] = useState(false);
  const [goUpdate, setGoUpdate] = useState(false);
  const [newObject, setNewObject] = useState({});

  const { saveState, loading: saveLoading } = useSave({
    url: categoriesURL,
    goSave,
    setGoSave,
    newObject,
    navigateAfter: "/categories",
  });

  const { state: updateState, loading: updateLoading } = useUpdate(
    `${categoriesURL}/${categoryId}`,
    goUpdate,
    setGoUpdate,
    newObject,
    `/categories/${categoryId}`
  );

  const loading = isEdit ? updateLoading : saveLoading;

  // Initialize prices object for all currencies
  const initializePrices = (existingPrices = []) => {
    const pricesObj = {};
    if (currencies && currencies.length > 0) {
      currencies.forEach((currency) => {
        // Find existing price for this currency
        const existingPrice = existingPrices.find(
          (p) => p.priceAmount?.currency?.id == currency.id
        );
        pricesObj[`price_${currency.id}`] = existingPrice
          ? existingPrice.priceAmount.amountCents / 100
          : "";
      });
    }
    return pricesObj;
  };

  // Transform category data to form initial values
  const transformCategoryToFormValues = (categoryData) => {
    if (!categoryData || !categoryData.categoryPrice) {
      return {
        name: "",
        paymentType: "",
        active: true,
        teamPrices: initializePrices(),
        memberPrices: initializePrices(),
        minMemberCount: "",
        maxMemberCount: "",
        membersGroupPrices: [],
      };
    }

    const paymentType = categoryData.categoryPrice.type;
    const formValues = {
      name: categoryData.name || "",
      paymentType: paymentType || "",
      active: categoryData.active !== undefined ? categoryData.active : true,
      teamPrices: initializePrices(),
      memberPrices: initializePrices(),
      minMemberCount: "",
      maxMemberCount: "",
      membersGroupPrices: [],
    };

    if (paymentType === "team" && categoryData.categoryPrice.teamPrices) {
      formValues.teamPrices = initializePrices(
        categoryData.categoryPrice.teamPrices
      );
    } else if (
      paymentType === "member" &&
      categoryData.categoryPrice.memberPrices
    ) {
      formValues.memberPrices = initializePrices(
        categoryData.categoryPrice.memberPrices
      );
    } else if (
      paymentType === "membersGroup" &&
      categoryData.categoryPrice.membersGroupPrices
    ) {
      // Extract min and max from existing data
      const counts = categoryData.categoryPrice.membersGroupPrices
        .map((item) => parseInt(item.count))
        .filter((count) => !isNaN(count));

      if (counts.length > 0) {
        formValues.minMemberCount = Math.min(...counts);
        formValues.maxMemberCount = Math.max(...counts);
      }

      // Transform existing prices to maintain them
      formValues.membersGroupPrices =
        categoryData.categoryPrice.membersGroupPrices.map((item) => ({
          count: item.count || "",
          prices: initializePrices(item.membersGroupCountPrice || []),
        }));
    }

    return formValues;
  };

  const transformFormDataToAPI = (values) => {
    const baseData = {
      name: values.name,
      active: values.active,
      categoryPrice: {
        type: values.paymentType,
      },
    };

    // Transform prices object to API format
    const transformPricesToAPI = (pricesObj) => {
      return currencies
        .filter((currency) => pricesObj[`price_${currency.id}`] !== "")
        .map((currency) => ({
          priceAmount: {
            amountCents: Math.round(pricesObj[`price_${currency.id}`] * 100),
            currencyId: currency.id,
          },
        }));
    };

    if (values.paymentType === "team") {
      baseData.categoryPrice.teamPrices = transformPricesToAPI(
        values.teamPrices
      );
    } else if (values.paymentType === "member") {
      baseData.categoryPrice.memberPrices = transformPricesToAPI(
        values.memberPrices
      );
    } else if (values.paymentType === "membersGroup") {
      baseData.categoryPrice.membersGroupPrices = values.membersGroupPrices.map(
        (item) => ({
          count: item.count,
          membersGroupCountPrice: transformPricesToAPI(item.prices),
        })
      );
    }

    return baseData;
  };

  const initialFormValues =
    isEdit && category
      ? transformCategoryToFormValues(category)
      : {
          name: "",
          paymentType: "",
          active: true,
          teamPrices: initializePrices(),
          memberPrices: initializePrices(),
          minMemberCount: "",
          maxMemberCount: "",
          membersGroupPrices: [],
        };

  if (isEdit && categoryLoading) {
    return (
      <div className="mainContainer">
        <div className="createCategoryContainer">
          <div className="loadingBox">
            <BarLoader color="var(--primary)" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mainContainer">
      <div className="createCategoryContainer">
        <div className="createCategoryHeader">
          <h2>{isEdit ? "Edit Category" : "Create New Category"}</h2>
        </div>

        <Formik
          initialValues={initialFormValues}
          enableReinitialize={
            isEdit || (!currenciesLoading && currencies.length > 0)
          }
          validationSchema={baseCreateCategorySchema}
          onSubmit={(values) => {
            const apiData = transformFormDataToAPI(values);
            setNewObject(apiData);
            if (isEdit) {
              setGoUpdate(true);
            } else {
              setGoSave(true);
            }
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
          }) => (
            <form className="createCategoryForm" onSubmit={handleSubmit}>
              <AutoGenerateMemberGroups initializePrices={initializePrices} />
              {/* Category Name */}
              <Box sx={{ display: "flex", alignItems: "center", gap: "40px" }}>
                <div className="formField" style={{ width: "100%" }}>
                  <TextFieldInput
                    fullWidth
                    name="name"
                    type="text"
                    placeholder="Enter category name"
                    className="categoryInput"
                    label="Category Name"
                    Required={true}
                    change={handleChange}
                    blur={handleBlur}
                    newValue={values.name}
                  />
                  {errors.name && touched.name && (
                    <p className="error">{errors.name}</p>
                  )}
                </div>

                {/* Active Status */}
                <div className="formField">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.active}
                        onChange={(e) =>
                          setFieldValue("active", e.target.checked)
                        }
                        color="primary"
                      />
                    }
                    label={values.active ? "Active" : "Inactive"}
                  />
                </div>
              </Box>
              {/* Payment Type */}
              <div className="formField">
                <FormControl fullWidth>
                  <InputLabel>Payment Type *</InputLabel>
                  <Select
                    name="paymentType"
                    value={values.paymentType}
                    label="Payment Type *"
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    <MenuItem value="team">Team</MenuItem>
                    <MenuItem value="member">Member</MenuItem>
                    <MenuItem value="membersGroup">Members Group</MenuItem>
                  </Select>
                </FormControl>
                {errors.paymentType && touched.paymentType && (
                  <p className="error">{errors.paymentType}</p>
                )}
              </div>

              {/* Conditional Fields */}
              {values.paymentType === "team" && (
                <div className="formField">
                  <label className="sectionLabel">
                    Team Prices (Enter price for each currency)
                  </label>
                  {currenciesLoading ? (
                    <p>Loading currencies...</p>
                  ) : currencies.length > 0 ? (
                    <div className="currencyPricesGrid">
                      {currencies.map((currency) => (
                        <TextFieldInput
                          key={currency.id}
                          fullWidth
                          name={`teamPrices.price_${currency.id}`}
                          type="number"
                          placeholder={`Enter price in ${
                            currency.code || currency.name
                          }`}
                          className="categoryInput"
                          label={`Price (${currency.code || currency.name})`}
                          Required={true}
                          change={(e) => {
                            setFieldValue(
                              `teamPrices.price_${currency.id}`,
                              e.target.value
                            );
                          }}
                          blur={handleBlur}
                          newValue={
                            values.teamPrices?.[`price_${currency.id}`] || ""
                          }
                          inputProps={{ step: "0.01", min: "0" }}
                        />
                      ))}
                    </div>
                  ) : (
                    <p className="error">No currencies available</p>
                  )}
                  {errors.teamPrices && touched.teamPrices && (
                    <p className="error">
                      Please enter prices for all currencies
                    </p>
                  )}
                </div>
              )}

              {values.paymentType === "member" && (
                <div className="formField">
                  <label className="sectionLabel">
                    Member Prices (Enter price for each currency)
                  </label>
                  {currenciesLoading ? (
                    <p>Loading currencies...</p>
                  ) : currencies.length > 0 ? (
                    <div className="currencyPricesGrid">
                      {currencies.map((currency) => (
                        <TextFieldInput
                          key={currency.id}
                          fullWidth
                          name={`memberPrices.price_${currency.id}`}
                          type="number"
                          placeholder={`Enter price in ${
                            currency.code || currency.name
                          }`}
                          className="categoryInput"
                          label={`Price (${currency.code || currency.name})`}
                          Required={true}
                          change={(e) => {
                            setFieldValue(
                              `memberPrices.price_${currency.id}`,
                              e.target.value
                            );
                          }}
                          blur={handleBlur}
                          newValue={
                            values.memberPrices?.[`price_${currency.id}`] || ""
                          }
                          inputProps={{ step: "0.01", min: "0" }}
                        />
                      ))}
                    </div>
                  ) : (
                    <p className="error">No currencies available</p>
                  )}
                  {errors.memberPrices && touched.memberPrices && (
                    <p className="error">
                      Please enter prices for all currencies
                    </p>
                  )}
                </div>
              )}

              {values.paymentType === "membersGroup" && (
                <div className="formField">
                  <label className="sectionLabel">Members Group Prices</label>

                  {/* Min and Max Member Count Inputs */}
                  <div
                    className="membersGroupInputs"
                    style={{ marginBottom: "20px" }}
                  >
                    <div>
                      <TextFieldInput
                        fullWidth
                        name="minMemberCount"
                        type="number"
                        placeholder="Min member count"
                        className="categoryInput"
                        label="Min Member Count"
                        Required={true}
                        change={handleChange}
                        blur={handleBlur}
                        newValue={values.minMemberCount}
                        inputProps={{ min: "1", step: "1" }}
                      />
                      {errors.minMemberCount && touched.minMemberCount && (
                        <p className="error">{errors.minMemberCount}</p>
                      )}
                    </div>
                    <div>
                      <TextFieldInput
                        fullWidth
                        name="maxMemberCount"
                        type="number"
                        placeholder="Max member count"
                        className="categoryInput"
                        label="Max Member Count"
                        Required={true}
                        change={handleChange}
                        blur={handleBlur}
                        newValue={values.maxMemberCount}
                        inputProps={{ min: "1", step: "1" }}
                      />
                      {errors.maxMemberCount && touched.maxMemberCount && (
                        <p className="error">{errors.maxMemberCount}</p>
                      )}
                    </div>
                  </div>

                  {/* Auto-generated Member Count Entries */}
                  <div className="memberGroupGrid">
                    {values.membersGroupPrices.length > 0 && (
                      <>
                        {values.membersGroupPrices.map((item, index) => (
                          <div key={index} className="membersGroupEntry">
                            <div className="membersGroupInputs">
                              <div>
                                <TextFieldInput
                                  fullWidth
                                  name={`membersGroupPrices.${index}.count`}
                                  type="number"
                                  placeholder="Member count"
                                  className="categoryInput"
                                  label="Member Count"
                                  Required={true}
                                  change={handleChange}
                                  blur={handleBlur}
                                  newValue={item.count}
                                  inputProps={{ min: "1", step: "1" }}
                                  disabled={true}
                                />
                              </div>
                            </div>

                            <div className="currencyPricesSection">
                              <label
                                className="sectionLabel"
                                style={{
                                  display: "block",
                                  marginBottom: "20px",
                                  marginTop: "0px",
                                }}
                              >
                                Prices (Enter price for each currency)
                              </label>
                              {currenciesLoading ? (
                                <p>Loading currencies...</p>
                              ) : currencies.length > 0 ? (
                                <div className="currencyPricesGrid">
                                  {currencies.map((currency) => (
                                    <TextFieldInput
                                      key={currency.id}
                                      fullWidth
                                      name={`membersGroupPrices.${index}.prices.price_${currency.id}`}
                                      type="number"
                                      placeholder={`Price in ${
                                        currency.code || currency.name
                                      }`}
                                      className="categoryInput"
                                      label={`Price (${
                                        currency.code || currency.name
                                      })`}
                                      Required={true}
                                      change={(e) => {
                                        setFieldValue(
                                          `membersGroupPrices.${index}.prices.price_${currency.id}`,
                                          e.target.value
                                        );
                                      }}
                                      blur={handleBlur}
                                      newValue={
                                        item.prices?.[`price_${currency.id}`] ||
                                        ""
                                      }
                                      inputProps={{ step: "0.01", min: "0" }}
                                    />
                                  ))}
                                </div>
                              ) : (
                                <p className="error">No currencies available</p>
                              )}
                              {errors.membersGroupPrices?.[index]?.prices &&
                                touched.membersGroupPrices?.[index]?.prices && (
                                  <p className="error">
                                    Please enter prices for all currencies
                                  </p>
                                )}
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                  </div>

                  {values.membersGroupPrices &&
                    typeof values.membersGroupPrices === "string" && (
                      <p className="error">{values.membersGroupPrices}</p>
                    )}
                  {errors.membersGroupPrices &&
                    typeof errors.membersGroupPrices === "string" && (
                      <p className="error">{errors.membersGroupPrices}</p>
                    )}
                </div>
              )}

              {/* Submit Buttons */}
              <div className="formActions">
                <PrimaryButton
                  classBtn={"center cancelButton"}
                  text={"Cancel"}
                  style={{
                    textTransform: "capitalize",
                    borderRadius: "5px",
                    color: "var(--primary)",
                    backgroundColor: "white",
                    border: "1px solid var(--primary)",
                    height: "45px",
                    padding: "0 30px",
                    minWidth: "120px",
                  }}
                  click={(e) => {
                    e.preventDefault();
                    navigate(
                      isEdit ? `/categories/${categoryId}` : "/categories"
                    );
                  }}
                />
                <PrimaryButton
                  classBtn={"center submitButton"}
                  text={isEdit ? "Update Category" : "Create Category"}
                  type="submit"
                  loading={loading}
                  disabled={loading}
                  style={{
                    textTransform: "capitalize",
                    borderRadius: "5px",
                    color: "white",
                    backgroundColor: "var(--primary)",
                    height: "45px",
                    padding: "0 30px",
                    minWidth: "150px",
                  }}
                />
              </div>
            </form>
          )}
        </Formik>
      </div>
    </div>
  );
}

export default CreateCategory;
